import React, { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DevopsHubHeader from "../components/devops/DevopsHubHeader";
import DevopsEntityCard from "../components/devops/DevopsEntityCard";
import { devopsService } from "../api/services/devopsService";
import { useAuth } from "../contexts/AuthContext";
import { useDevopsWorkspace } from "../contexts/DevopsWorkspaceContext";
import type { DevopsApplication } from "../types/devops";
import { canMaintainApplication } from "../utils/devopsAccess";
import { devopsRoutes } from "../utils/devopsRoutes";

const ApplicationsHubPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roles = user?.roles ?? [];
  const { bindEntity } = useDevopsWorkspace();
  const [items, setItems] = useState<DevopsApplication[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canWrite = canMaintainApplication(roles);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const rows = await devopsService.listApplications();
        if (!cancelled) setItems(rows);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load applications");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) =>
      [item.name, item.applicationKind, item.deploymentType, item.owner, item.sourceRepository ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [items, search]);

  return (
    <>
      <DevopsHubHeader
        title="Applications"
        description="Catalog applications independent of environment. DevOps operators can add and edit; either role can map them into environments."
        search={search}
        onSearchChange={setSearch}
        onAdd={canWrite ? () => navigate(devopsRoutes.applicationNew) : undefined}
        addLabel="Add application"
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Grid container spacing={2}>
          {filtered.map((item) => (
            <Grid item xs={12} sm={6} key={item.uuid}>
              <DevopsEntityCard
                title={item.name}
                subtitle={`${item.applicationKind} · ${item.deploymentType}`}
                status={item.status}
                metadata={
                  <Typography variant="caption" color="text.secondary">
                    Owner {item.owner}
                    {(item.environments ?? []).length
                      ? ` · ${(item.environments ?? []).map((env) => env.name).join(", ")}`
                      : " · not mapped"}
                  </Typography>
                }
                onView={() => navigate(devopsRoutes.applicationView(item.uuid))}
                onAskAgent={() =>
                  bindEntity({ type: "APPLICATION", uuid: item.uuid, name: item.name })
                }
                onEdit={canWrite ? () => navigate(devopsRoutes.applicationEdit(item.uuid)) : undefined}
                onDelete={
                  canWrite
                    ? async () => {
                        if (!window.confirm(`Delete application ${item.name}?`)) return;
                        await devopsService.deleteApplication(item.uuid);
                        setItems((prev) => prev.filter((row) => row.uuid !== item.uuid));
                      }
                    : undefined
                }
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No applications match your search.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default ApplicationsHubPage;
